// BRIEF 86 PHASE 2, STEP 6 (run by brief 90 §5) — the Agenda section, once.
//
// The lowest-similarity pair in the brief's table (54% when it was written, 65%
// after this step's two harmonisation commits), and the number was misleading in
// both directions: most of the distance was ONE STRUCTURAL CHOICE — CaseView
// renders the SectionHeader before its IIFE, CCV renders it inside the fragment
// the IIFE returns. Same header, same order on screen, and a line-diff cannot
// say so. What was genuinely different was small and is listed here.
//
//   THE CONTAINER DISCRIMINATOR — `caId` vs `ccId` on the events the pane reads
//   and on the payload its "+ Add" opens with, and the header's DOM id
//   (`cag-<id>` / `cc-agenda-<id>`). Carried as `kind`, the same shape step 4
//   established (`CaseTasksSection.tsx:361`).
//
//   THE HOST'S OWN HANDLES — `om()` vs `globalOm()` as `openModal`, and the
//   free-name pair `sAgMy`/`setAgMy` for one setter.
//
//   THE DERIVATION SITE — CaseView derives `cag` at component level
//   (`CaseView.tsx:1041`), CCV inside the section. Neither is more correct;
//   `cag` is a prop and each host derives it where it already did. Same class as
//   step 1's `cteVis0`/`visTe0`.
//
// ── WHAT THE TWO HARMONISATION COMMITS FOUND ───────────────────────────────
//
// CaseView's "+ Add" opened an event modal that knew nothing about the case it
// was opened from — no date, no assignees, a live matter picker. CCV's filled
// all three in. That was a decision made once, on one side, so it travelled
// TOWARD CaseView (§3.6's precedent for the empty-state sentence).
//
// CCV printed TWO empty-state sentences at once on a case with no events: the
// composed one, plus a trailing `cc.empty.no_agenda_events` paragraph. And its
// week filter read a key family (`agenda.filter.*`) whose only reader in the app
// was this pane, where Dashboard, AgendaView, ReportsView and CaseView all read
// `agenda.range.*`. Both resolved toward CaseView before the move.
//
// The `genLoc` note CCV carried here is not brought across: it explained why a
// CCV-LOCAL copy of App.tsx's location ladder is absent, and the ladder it named
// was removed with `resolveEventLocation` (src/utils.ts) long before this. A
// component shared by both views is the wrong place to keep one view's account
// of something neither has.
// 210 §1 — `import React`, the house remedy for react/react-in-jsx-scope
// (SharedUI.tsx says so): the not-loaded wrap is one more JSX element.
import React from "react";
import { useState } from "react";
import type { ReactNode } from "react";
import { ChevronsUpDown, ChevronsDownUp } from "lucide-react";
import CaseEventRow from "./CaseEventRow";
import {
  SectionHeader,
  AddButton,
  LockButton,
  MyAllToggle,
  SegmentedControl,
  ListNotLoaded, // 210 §1
} from "./SharedUI";
import Pager from "./Pager";
import { pageOf } from "../hooks/usePager";
import { FS } from "../lib/tokens";
import type { TFn } from "../lib/taxonomyTypes";

// The read-sets this pane uses. Partial by design, and typed rather than `any`
// because the ratchet allows a new file none.
type AgEvent = {
  id: number;
  date?: string | null;
  title?: string | null;
  dlEventId?: number | null;
  assignedCollaborators?: number[];
  [k: string]: unknown;
};
type Person = { id: number; [k: string]: unknown };

type Props = {
  // 'case' | 'cc' — the container discriminator, and the only thing the pane
  // asks about which host it is in.
  kind: "case" | "cc";
  /* 190 glance round 2 [A], RULED: while the case Bento is open, the Bento
     pane covers the two content areas UNDER the section titles and filters —
     this section keeps its header row exactly as it is and renders no body,
     so the pane (CaseTasksSection's, spanning both columns) meets nothing
     underneath. Host state, beside `tkMatrix`. */
  covered?: boolean;
  cs: { id: number; caseCollaborators?: { coId?: number }[]; [k: string]: unknown };
  // This matter's events, sorted by date — derived by each host where it
  // already derived it.
  cag: AgEvent[];
  // 109 §7f — the case_stage entries for the event row's link pill.
  caseStageEntries?: unknown;
  co: Person[];
  currentUser?: number | null;
  ag: AgEvent[];
  sAg: unknown;
  s: (...a: unknown[]) => void;
  SK: Record<string, string>;
  agRef: { current: AgEvent[] };
  coAvs: unknown;
  evtTypes: unknown;
  etTag: unknown;
  /* 139 §1 — the longest code the event taxonomy can show, resolved once in
     App and forwarded to the row that mirrors it. Typed, though its neighbours
     are `unknown`: rule 4 types what is new whatever the bag around it is. */
  evtLongestCode: string;
  locIcon: unknown;
  MultiSelect: unknown;
  _ITEM_LEVEL?: unknown;
  itemLevelList?: unknown;
  evExpanded: unknown;
  // 110 §1h — the bulk setter for open-all/close-all (the per-id toggler
  // beside it cannot address the whole list).
  setEvExpanded: (v: Set<unknown>) => void;
  toggleEvExpanded: unknown;
  agMy: boolean;
  setAgMy: (v: boolean) => void;
  agWeek: string;
  setAgWeek: (v: string) => void;
  agPage: number;
  setAgPage: (v: number) => void;
  agLocked: boolean;
  setAgLocked: (fn: (v: boolean) => boolean) => void;
  canEditAgenda?: boolean;
  canEditOthersTasks?: boolean;
  /* BRIEF 91 §3a — forwarded to CaseEventRow, which builds the event
     drawer's timer column from them (with `openModal` below). Both hosts
     already hand the identical pair to CaseTasksSection next door. */
  activeTimers?: unknown;
  startTimer?: (taskId?: unknown, subtaskId?: unknown, opts?: unknown) => void;
  confirmAndUndo: (o: {
    label?: unknown;
    archive?: unknown;
    toastName?: unknown;
    onConfirm: () => void;
    undo?: {
      snapshot: { ev: AgEvent; dl: AgEvent | null };
      restore: (snap: { ev: AgEvent; dl: AgEvent | null }) => void;
    } | null;
  }) => void;
  openModal: (kind: string, payload: Record<string, unknown>) => void;
  sCol: (k: string) => boolean;
  tog: (k: string) => void;
  t: TFn;
};

export default function CaseAgendaSection({
  kind,
  cs,
  cag,
  /* 109 §7f — the case_stage entries for the event row's link pill; the
     stages themselves come off `cs`, which this section already holds. */
  caseStageEntries,
  co,
  currentUser,
  ag,
  sAg,
  s,
  SK,
  agRef,
  coAvs,
  evtTypes,
  etTag,
  evtLongestCode,
  locIcon,
  MultiSelect,
  _ITEM_LEVEL,
  itemLevelList,
  evExpanded,
  setEvExpanded,
  toggleEvExpanded,
  agMy,
  setAgMy,
  agWeek,
  setAgWeek,
  agPage,
  setAgPage,
  agLocked,
  setAgLocked,
  canEditAgenda,
  canEditOthersTasks,
  activeTimers,
  startTimer,
  confirmAndUndo,
  openModal,
  sCol,
  tog,
  t,
  covered = false,
}: Props): ReactNode {
  /* 110 §1h — the same one-toggle sweep the Tasks header has; see
     CaseTasksSection. Event subtasks are host state (evExpanded, set in
     bulk); docs/notes are row-local and ride the {seq, open} tick. */
  const [drawersCast, setDrawersCast] = useState({ seq: 0, open: false });
  return (
    <div
      className="csSec-agenda"
      style={{ flex: "1 1 400px", minWidth: 0 }}
      data-secollapsed={sCol("agenda") ? "1" : "0"}
    >
      <SectionHeader
        id={(kind === "cc" ? "cc-agenda-" : "cag-") + cs.id}
        title={t("case.agenda")}
        collapsible
        open={!sCol("agenda")}
        onToggle={() => tog("agenda")}
        leftContent={
          <>
            {currentUser && (
              <MyAllToggle
                my={agMy}
                onMy={() => setAgMy(true)}
                onAll={() => setAgMy(false)}
                counts={{
                  my: cag.filter((ev: AgEvent) => {
                    const ass = ev.assignedCollaborators || [];
                    return ass.includes(currentUser);
                  }).length,
                  all: cag.length,
                }}
              />
            )}
            <SegmentedControl
              value={agWeek}
              options={[
                ["all", t("common.all")],
                ["last", t("agenda.range.last")],
                ["this", t("agenda.range.this_week")],
                ["next", t("agenda.range.next")],
              ]}
              onChange={(k: string) => {
                setAgWeek(k);
                setAgPage(0);
              }}
              counts={{ all: cag.length }}
            />
            {/* 110 §1h — open/close every card's drawers. */}
            <button
              type="button"
              title={
                drawersCast.open
                  ? t("drawers.close_all", { fallback: "Close all drawers" })
                  : t("drawers.open_all", { fallback: "Open all drawers" })
              }
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 2,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: drawersCast.open ? "var(--ac)" : "var(--tp)",
                opacity: drawersCast.open ? 1 : 0.6,
              }}
              onClick={() => {
                const open = !drawersCast.open;
                setDrawersCast((p) => ({ seq: p.seq + 1, open }));
                /* 111 §2 — OPEN only cards that HAVE subtasks (the counter's
                   non-archived fact, SubtaskCounter.tsx:85); CLOSE stays
                   total. Docs/notes gate themselves in the row's tick. */
                setEvExpanded(
                  open
                    ? new Set(
                        cag
                          .filter(
                            (ev: AgEvent) =>
                              (Array.isArray(ev.subtasks)
                                ? ev.subtasks
                                : []
                              ).filter(
                                (s: { archived?: boolean } | null) =>
                                  s && !s.archived,
                              ).length > 0,
                          )
                          .map((ev: AgEvent) => ev.id),
                      )
                    : new Set(),
                );
              }}
            >
              {drawersCast.open ? (
                <ChevronsDownUp size={14} />
              ) : (
                <ChevronsUpDown size={14} />
              )}
            </button>
          </>
        }
        rightContent={
          <>
            <AddButton
              label={t("common.add_button.add", { fallback: "Add" })}
              onClick={() =>
                openModal("ag", {
                  ...(kind === "cc" ? { ccId: cs.id } : { caId: cs.id }),
                  date: new Date().toISOString().slice(0, 10),
                  assignedCollaborators: (cs.caseCollaborators || [])
                    .map((x: { coId?: number }) =>
                      co.find((m: Person) => m.id === x.coId),
                    )
                    .filter(Boolean)
                    .map((c) => (c as Person).id),
                  _caseFixed: true,
                })
              }
              visible={!agLocked && canEditAgenda}
            />
            <LockButton
              locked={agLocked}
              onToggle={() => setAgLocked((v) => !v)}
            />
          </>
        }
      />
      {/* 210 §1 — while the agenda could not load, the body says so in its place
          (ListNotLoaded) — never an empty week. */}
      <ListNotLoaded lists={[SK.ag]}>
      {(() => {
        if (covered) return null;
        const _now = new Date();
        const _dow = _now.getDay();
        const _monD = new Date(_now);
        _monD.setDate(_now.getDate() - (_dow === 0 ? 6 : _dow - 1));
        const _monS = _monD.toISOString().slice(0, 10);
        const _sunD = new Date(_monD);
        _sunD.setDate(_monD.getDate() + 6);
        const _sunS = _sunD.toISOString().slice(0, 10);
        const _lastMonD = new Date(_monD);
        _lastMonD.setDate(_monD.getDate() - 7);
        const _lastMonS = _lastMonD.toISOString().slice(0, 10);
        const _lastSunD = new Date(_lastMonD);
        _lastSunD.setDate(_lastMonD.getDate() + 6);
        const _lastSunS = _lastSunD.toISOString().slice(0, 10);
        const _nextMonD = new Date(_monD);
        _nextMonD.setDate(_monD.getDate() + 7);
        const _nextMonS = _nextMonD.toISOString().slice(0, 10);
        const _nextSunD = new Date(_nextMonD);
        _nextSunD.setDate(_nextMonD.getDate() + 6);
        const _nextSunS = _nextSunD.toISOString().slice(0, 10);
        const _wkStart =
          agWeek === "last"
            ? _lastMonS
            : agWeek === "next"
              ? _nextMonS
              : _monS;
        const _wkEnd =
          agWeek === "last"
            ? _lastSunS
            : agWeek === "next"
              ? _nextSunS
              : _sunS;
        const cagAll = (
          agWeek === "all"
            ? cag.filter((ev: AgEvent) => ev.date)
            : cag.filter(
                (ev: AgEvent) =>
                  ev.date &&
                  ev.date.slice(0, 10) >= _wkStart &&
                  ev.date.slice(0, 10) <= _wkEnd,
              )
        ).filter((ev: AgEvent) => {
          if (!agMy || !currentUser) return true;
          const ass = ev.assignedCollaborators || [];
          return !ass.length || ass.includes(currentUser);
        });
        const cagF = cagAll;
        return (
          <>
            {/* Brief 86 §3.6 — the empty-state sentence, resolved in
                PHASE 1 as the brief directs. RULINGS.md 2026-08-16
                recorded it as drift and not design: "the collective
                agenda shows 'No events assigned to you this week'; the
                case agenda shows blank." The collective side's is the
                better behaviour, so it comes here rather than the blank
                going there — the one place in this brief where the
                collective view is authoritative, and §0 allows exactly
                that ("where the difference was NOBODY'S DECISION, CV is
                authoritative" — this one was a decision, made once, on
                one side).

                Composed from the SAME key and the same four terms as
                CollectiveCaseView.tsx's, so the two screens cannot
                drift into two wordings; `cc.agenda.empty` is reused
                rather than a `case.agenda.empty` minted, because one
                sentence with one meaning is one key (brief 59). */}
            {cagF.length === 0 && (
              <p
                style={{
                  fontSize: FS.s11,
                  color: "var(--tm)",
                  marginBottom: 8,
                }}
              >
                {t("cc.agenda.empty", {
                  week:
                    agWeek === "all"
                      ? ""
                      : agWeek === "last"
                        ? t("agenda.filter.last_week", {
                            fallback: " last week",
                          })
                        : agWeek === "next"
                          ? t("agenda.filter.next_week", {
                              fallback: " next week",
                            })
                          : t("agenda.filter.this_week_qual", {
                              fallback: " this week",
                            }),
                  mine: agMy
                    ? t("cc.agenda.assigned_to_you", {
                        fallback: " assigned to you",
                      })
                    : "",
                  fallback:
                    "No events" +
                    (agWeek === "all"
                      ? ""
                      : agWeek === "last"
                        ? " last week"
                        : agWeek === "next"
                          ? " next week"
                          : " this week") +
                    (agMy ? " assigned to you" : "") +
                    ".",
                })}
              </p>
            )}
            {(() => {
              /* 188 D.3 — the arithmetic is `pageOf`'s (ten; `Math.max(1, ceil)` ·
                 `Math.min(page, tot - 1)` · the slice — this copy's own three
                 expressions, identity replayed over 4,800 cases, 0 differences);
                 the page state stays CaseView's `agPage`, as before. */
              const { cur: __cur, tot: __tot, pageItems: __pg } = pageOf(cagF, agPage);
              return (
                <>
                  {__pg.map((ev: AgEvent) => (
                    <CaseEventRow
                      key={ev.id}
                      drawersCast={drawersCast} // 110 §1h
                      /* BRIEF 98 §4 — as CaseTasksSection does, and for the
                         same reason: this section's own subject, forwarded. */
                      oneDriveMatter={{ kind: kind === "cc" ? "cc" : "ca", matter: cs }}
                      matterRow={cs} // 111 §5c — itemLinks feed
                      /* 109 §7f — the pill's two feeds. */
                      stages={cs.stages || []}
                      caseStageEntries={caseStageEntries}
                      ev={ev}
                      co={co}
                      currentUser={currentUser}
                      todayStr={new Date().toISOString().slice(0, 10)}
                      priMap={_ITEM_LEVEL}
                      itemLevelList={itemLevelList}
                      evtTypes={evtTypes}
                      etTag={etTag}
                      evtLongestCode={evtLongestCode}
                      coAvs={coAvs}
                      locIcon={locIcon}
                      MultiSelect={MultiSelect}
                      ag={ag}
                      sAg={sAg}
                      s={s}
                      SK={SK}
                      evExpanded={evExpanded}
                      toggleEvExpanded={toggleEvExpanded}
                      agLocked={agLocked}
                      canEditAgenda={canEditAgenda}
                      canEditOthersTasks={canEditOthersTasks}
                      activeTimers={activeTimers}
                      startTimer={startTimer}
                      openModal={openModal}
                      onDeleteEvent={(_ev: AgEvent) =>
                        confirmAndUndo({
                          label: "this event",
                          archive: true,
                          toastName: `Archived "${_ev.title || "event"}"`,
                          onConfirm: () =>
                            s(
                              SK.ag,
                              (ag || []).filter(
                                (a: AgEvent) =>
                                  a.id !== _ev.id &&
                                  a.id !== _ev.dlEventId,
                              ),
                              sAg,
                            ),
                          undo: {
                            snapshot: {
                              ev: _ev,
                              dl: _ev.dlEventId
                                ? (ag || []).find(
                                    (a: AgEvent) => a.id === _ev.dlEventId,
                                  ) || null
                                : null,
                            },
                            restore: (snap) => {
                              const adds = snap.dl
                                ? [snap.ev, snap.dl]
                                : [snap.ev];
                              s(SK.ag, [...agRef.current, ...adds], sAg);
                            },
                          },
                        })
                      }
                    />
                  ))}
                  {__tot > 1 && <Pager cur={__cur} tot={__tot} setPage={setAgPage} />}
                </>
              );
            })()}
          </>
        );
      })()}
      </ListNotLoaded>
    </div>
  );
}
